import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contextProviders/AuthProvider';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface PublicRouteProps {
  children: React.ReactElement;
  redirectIfAuthenticated?: boolean;
}

/**
 * PublicRoute - Para rutas públicas (accesibles sin autenticación)
 * 
 * Flujo:
 * 1. Si loading → Muestra spinner (verificando autenticación)
 * 2. Si autenticado Y redirectIfAuthenticated=true → Redirect a /dashboard
 * 3. Sino → Muestra el componente hijo
 * 
 * Uso:
 * 
 * // Landing page (permite acceso aunque esté autenticado)
 * <Route path="/" element={
 *   <PublicRoute>
 *     <Landing />
 *   </PublicRoute>
 * } />
 * 
 * // Login (redirect si ya está autenticado)
 * <Route path="/login" element={
 *   <PublicRoute redirectIfAuthenticated={true}>
 *     <Login />
 *   </PublicRoute>
 * } />
 */
const PublicRoute: React.FC<PublicRouteProps> = ({ 
  children, 
  redirectIfAuthenticated = false 
}) => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useTranslation();

  // Mientras verifica autenticación, mostrar spinner
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <Spin size="large" />
        <p style={{ color: '#666', fontSize: 14 }}>{translate(t, TRANSLATIONS.LAYOUT_LOADING)}</p>
      </div>
    );
  }

  // Si está autenticado y la ruta debe redirigir (ej: /login, /register)
  if (isAuthenticated && redirectIfAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Mostrar el componente público
  return children;
};

export default PublicRoute;